import { Fragment } from 'react';

import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import { formClasses } from './utils/FormStyles';
import { Label } from './utils/Label';

interface MultiSelectFieldProps {
  id: string;
  label?: string;
  options: string[];
  value: string[];
  onChange: (selectedOptions: string[]) => void;
  className?: string;
  error?: boolean;
  errorMessage?: string;
  maxSelections?: number;
  onBlur?: () => void;
}

export function MultiSelectField({
  id,
  label,
  options,
  value,
  onChange,
  className = '',
  error = false,
  errorMessage = '',
  maxSelections = Infinity,
  ...props
}: MultiSelectFieldProps) {
  const selectedOptionsText =
    value?.map((item) => item).join(', ') || 'Select options...';

  const handleSelectChange = (selectedOption: string[]) => {
    const isRemoved = value.length > selectedOption.length;

    if (value.length < maxSelections || isRemoved) {
      props.onBlur && props.onBlur();
      onChange(selectedOption);
    }
  };

  const renderedOptions = options.map((option, index) => {
    const isDisabled = value.length >= maxSelections && !value.includes(option);
    return (
      <Listbox.Option
        key={`${option}-${index}`}
        disabled={isDisabled}
        value={option}
        className={({ active, selected }) =>
          `relative select-none py-2 pl-3 pr-9 ${active ? 'bg-gray-100' : ''} ${
            selected ? 'bg-blue-50' : 'text-gray-900'
          } ${
            isDisabled && !selected ? 'cursor-not-allowed' : 'cursor-pointer'
          }`
        }
      >
        {({ selected }) => (
          <div className="flex items-center justify-between">
            <span>{option}</span>
            {selected && (
              <span className="absolute right-2">
                <CheckIcon className="h-5 w-5 text-gray-600" />
              </span>
            )}
          </div>
        )}
      </Listbox.Option>
    );
  });

  const buttonClassName = clsx(formClasses, 'text-left', {
    'border-red-500 focus:border-red-700': error,
    'text-gray-400': !value?.length,
    'text-gray-900': value?.length > 0,
  });

  return (
    <div className={className}>
      {label && <Label id={id}>{label}</Label>}
      <Listbox multiple value={value} onChange={handleSelectChange}>
        {({ open }) => (
          <div className="relative">
            <Listbox.Button
              className={clsx(buttonClassName, open && 'ring-2 ring-blue-500')}
            >
              {selectedOptionsText}
            </Listbox.Button>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              show={open}
            >
              <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {renderedOptions}
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
      {error && errorMessage && <p className="text-red-500">{errorMessage}</p>}
    </div>
  );
}
